import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Download, CalendarDays, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { cn } from '../lib/utils';
import { PageTransition } from '../components/PageTransition';
import { getApiBaseUrl } from '../lib/api-base-url';

interface MonthSummary {
  totalWorkedHours: number;
  totalOnCallHours: number;
  totalOvertimeHours: number;
  daysWorked: number;
  state: {
    state: string;
    updatedAt: string;
  } | null;
}

const MONTHS = Array.from({ length: 12 }, (_, i) => i);

const getStateVariant = (state: string | null): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (!state) return 'outline';
  switch (state.toLowerCase()) {
    case 'approved':
      return 'default';
    case 'rejected':
      return 'destructive';
    case 'in_approval':
      return 'secondary';
    default:
      return 'outline';
  }
};

const YearlySummary: React.FC = () => {
  const { t } = useTranslation();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [summaryData, setSummaryData] = useState<MonthSummary[]>([]);
  const [downloading, setDownloading] = useState<number | null>(null);

  const fetchYearlySummary = useCallback(async () => {
    try {
      const apiUrl = getApiBaseUrl();
      const response = await axios.get(
        `${apiUrl}/timesheets/yearly/${selectedYear}`
      );
      setSummaryData(response.data);
    } catch (error) {
      console.error('Error fetching yearly summary:', error);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchYearlySummary();
  }, [fetchYearlySummary]);

  const downloadPdf = useCallback(async (month: number) => {
    try {
      setDownloading(month);
      const apiUrl = getApiBaseUrl();
      const response = await axios.get(
        `${apiUrl}/timesheets/download-pdf?year=${selectedYear}&month=${month}`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `timesheet-${selectedYear}-${month}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      console.error('Failed to download PDF:', err);
    } finally {
      setDownloading(null);
    }
  }, [selectedYear]);

  const getYearOptions = useCallback(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - i);
  }, []);

  // Calculate totals
  const totals = summaryData.reduce(
    (acc, summary) => ({
      daysWorked: acc.daysWorked + (summary?.daysWorked || 0),
      totalWorkedHours: acc.totalWorkedHours + (summary?.totalWorkedHours || 0),
      totalOnCallHours: acc.totalOnCallHours + (summary?.totalOnCallHours || 0),
      totalOvertimeHours: acc.totalOvertimeHours + (summary?.totalOvertimeHours || 0),
    }),
    { daysWorked: 0, totalWorkedHours: 0, totalOnCallHours: 0, totalOvertimeHours: 0 }
  );

  return (
    <PageTransition>
    <div className="p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
            <CalendarDays className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('pages.yearlySummary.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('pages.yearlySummary.subtitle', 'View your monthly timesheet summaries')}
            </p>
          </div>
        </div>

        <Select
          value={String(selectedYear)}
          onValueChange={(value) => setSelectedYear(Number(value))}
        >
          <SelectTrigger className="w-full sm:w-[140px]">
            <SelectValue placeholder={t('common.year')} />
          </SelectTrigger>
          <SelectContent>
            {getYearOptions().map((year) => (
              <SelectItem key={year} value={String(year)}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t('common.daysWorked')}</p>
            <p className="text-2xl font-bold">{totals.daysWorked}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t('pages.yearlySummary.columns.totalWorked')}</p>
            <p className="text-2xl font-bold">{totals.totalWorkedHours.toFixed(1)}h</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t('pages.yearlySummary.columns.totalOnCall')}</p>
            <p className="text-2xl font-bold">{totals.totalOnCallHours.toFixed(1)}h</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t('pages.yearlySummary.columns.totalOvertime')}</p>
            <p className="text-2xl font-bold">{totals.totalOvertimeHours.toFixed(1)}h</p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileText className="h-5 w-5 text-cyan-500" />
            {t('pages.yearlySummary.monthlyBreakdown', 'Monthly Breakdown')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Phone: one block per month, no horizontal scroll */}
          <div className="space-y-2 md:hidden">
            {MONTHS.map((monthIndex) => {
              const summary = summaryData[monthIndex];
              const hasData = Boolean(summary && summary.daysWorked > 0);
              return (
                <div
                  key={monthIndex}
                  className={cn('rounded-lg border p-3', !hasData && 'text-muted-foreground')}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{t(`common.months.${monthIndex}`)}</span>
                    {summary?.state ? (
                      <Badge variant={getStateVariant(summary.state.state)}>
                        {t(`timesheet.states.${summary.state.state.toLowerCase()}`)}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">-</span>
                    )}
                  </div>
                  <dl className="mt-2 grid grid-cols-4 gap-1 text-center text-[11px] leading-tight">
                    <div>
                      <dt className="text-muted-foreground">{t('timesheet.daysWorked', 'Giorni')}</dt>
                      <dd className="font-semibold">{summary?.daysWorked ?? 0}</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">{t('timesheet.columns.workedHours', 'Lavorate')}</dt>
                      <dd className="font-semibold">{(summary?.totalWorkedHours ?? 0).toFixed(1)}h</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">{t('timesheet.columns.onCallHours', 'Reperibilità')}</dt>
                      <dd className="font-semibold">{(summary?.totalOnCallHours ?? 0).toFixed(1)}h</dd>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">{t('timesheet.columns.overtimeHours', 'Straordinario')}</dt>
                      <dd className="font-semibold">{(summary?.totalOvertimeHours ?? 0).toFixed(1)}h</dd>
                    </div>
                  </dl>
                  {hasData && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => downloadPdf(monthIndex + 1)}
                      disabled={downloading === monthIndex + 1}
                      className="mt-3 w-full gap-2"
                    >
                      <Download className="h-4 w-4" />
                      {downloading === monthIndex + 1 ? t('common.downloading', 'Downloading...') : t('common.download')}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
          <Table className="hidden md:table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('pages.yearlySummary.columns.month')}</TableHead>
                <TableHead className="text-right">{t('common.daysWorked')}</TableHead>
                <TableHead className="text-right">{t('pages.yearlySummary.columns.totalWorked')}</TableHead>
                <TableHead className="text-right">{t('pages.yearlySummary.columns.totalOnCall')}</TableHead>
                <TableHead className="text-right">{t('pages.yearlySummary.columns.totalOvertime')}</TableHead>
                <TableHead className="text-center">{t('pages.yearlySummary.columns.state')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {MONTHS.map((monthIndex) => {
                const defaultSummary: MonthSummary = {
                  totalWorkedHours: 0,
                  totalOnCallHours: 0,
                  totalOvertimeHours: 0,
                  daysWorked: 0,
                  state: null,
                };
                const summary = summaryData[monthIndex] || defaultSummary;
                const hasData = summary.daysWorked > 0;

                return (
                  <TableRow
                    key={monthIndex}
                    className={cn(!hasData && 'text-muted-foreground')}
                  >
                    <TableCell className="font-medium">
                      {t(`common.months.${monthIndex}`)}
                    </TableCell>
                    <TableCell className="text-right">{summary.daysWorked}</TableCell>
                    <TableCell className="text-right">{summary.totalWorkedHours.toFixed(1)}</TableCell>
                    <TableCell className="text-right">{summary.totalOnCallHours.toFixed(1)}</TableCell>
                    <TableCell className="text-right">{summary.totalOvertimeHours.toFixed(1)}</TableCell>
                    <TableCell className="text-center">
                      {summary.state ? (
                        <Badge variant={getStateVariant(summary.state.state)}>
                          {t(`timesheet.states.${summary.state.state.toLowerCase()}`)}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadPdf(monthIndex + 1)}
                        disabled={downloading === monthIndex + 1 || !hasData}
                        className="gap-2"
                      >
                        <Download className="h-4 w-4" />
                        {downloading === monthIndex + 1 ? t('common.downloading', 'Downloading...') : t('common.download')}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
    </PageTransition>
  );
};

export default YearlySummary;
